import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import api from '../utils/authUtils';
import flyerPage1 from '../assets/hearts-match-flyer-1.jpg';
import flyerPage2 from '../assets/hearts-match-flyer-2.jpg';

// Sits between an event's "SUBMIT ABSTRACT" button and the actual submission
// form (components/submissions/SubmissionsPage.js) - mirrors how real medical
// conferences (ESC, ESMO, ...) separate a "Call for Abstracts" explainer from
// the submission tool itself, rather than dropping straight into a bare form.
// Reachable with or without ?eventId=<id>: with one (the normal path, carried
// from an event's button), the event is pre-selected; without one, the left
// column offers a picker over GET /api/submissions/events instead.

const PageWrapper = styled.div`
  padding-left: 2rem;
  padding-right: 2rem;

  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const Crumb = styled.nav`
  max-width: 1700px;
  margin: 0 auto;
  padding: 1.6rem 2rem 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.65);

  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  span.sep { margin: 0 0.4rem; }
  span.current { color: #fff; font-weight: bold; }
`;

const SectionWrapper = styled.section`
  background-color: #90C4D9;
  padding: 70px 20px 40px;
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    padding: 40px 0px 0px;
  }
`;

const SectionTitle = styled.h2`
  color: white;
  text-align: center;
  font-size: 3rem;
  margin-bottom: 10px;
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SectionSubTitle = styled.h2`
  color: white;
  text-align: center;
  font-size: 1.8rem;
  margin-bottom: 70px;
  @media (max-width: 1024px) {
    margin-bottom: 20px;
  }
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const EventWrapper = styled.div`
  background: transparent;
  padding: 20px;
  margin-bottom: 10px;
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 1700px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-right: 20px;

  @media (max-width: 1024px) {
    padding-right: 0;
    text-align: center;
    max-width: 600px;
    width: 100%;
  }
`;

const MiddleColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 1024px) {
    max-width: 600px;
    width: 100%;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-left: 20px;

  @media (max-width: 1024px) {
    padding-left: 0;
    max-width: 600px;
    width: 100%;
  }
`;

const Headline = styled.h1`
  font-size: 3rem;
  margin-top: 40px;
  margin-bottom: 20px;
  display: inline-block;
  padding-bottom: 6px;
  background-image: linear-gradient(to right, #4e4094, #14cbde);
  background-size: 100% 4px;
  background-position: 0 bottom;
  background-repeat: no-repeat;

  @media (max-width: 1024px) {
    font-size: 1.8rem;
    margin-top: 10px;
    text-align: center;
  }
`;

const IntroText = styled.p`
  font-size: 1.4rem;
  line-height: 1.5;
  margin-bottom: 20px;
  @media (max-width: 1024px) {
    font-size: 1rem;
  }
`;

const Tagline = styled.p`
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.4;
  margin-bottom: 20px;
  @media (max-width: 1024px) {
    font-size: 1.1rem;
  }
`;

const IntroNote = styled.p`
  font-size: 1.4rem;
  font-weight: bold;
  line-height: 1.5;
  margin-bottom: 20px;
  @media (max-width: 1024px) {
    font-size: 1rem;
  }
`;

const FlyerImage = styled.img`
  max-width: 100%;
  height: auto;
  margin-bottom: 20px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
`;

// Same treatment as SubmitAbstractButton in MainEvent.js/OtherHeartsEvents.js/
// EventDetail.js - "SUBMIT ABSTRACT" is English-only now (no more German
// "BEITRAG EINREICHEN" to wrap), so this matches their fixed-height,
// full-size styling instead of the old shrink-to-fit treatment, with no
// horizontal padding so the text has room to stay on one line.
const CtaButton = styled.button`
  background-color: #47ABDB;
  text-decoration: none;
  height: 4.0rem;
  font-weight: bold;
  color: white;
  border: none;
  padding: 10px 0;
  font-size: 1.8rem;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 100%;
  max-width: 300px;
  text-align: center;
  margin-bottom: 0.5rem;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const CtaHint = styled.p`
  font-size: 0.95rem;
  text-align: center;
  max-width: 300px;
  margin: 0;
`;

// Mirrors EventDetail's EventButtonTitle/EventDetails - a caption under the
// action button naming what it applies to, rather than a separate card.
const CtaCaption = styled.p`
  margin: 0.5rem 0 0;
  width: 100%;
  max-width: 300px;
  text-align: center;
  font-size: 1rem;
`;

const CtaCaptionMeta = styled.p`
  margin: 0.15rem 0 0;
  width: 100%;
  max-width: 300px;
  text-align: center;
  font-size: 0.85rem;
  color: #334155;
`;

const ChangeLink = styled.button`
  margin-top: 0.6rem;
  background: none;
  border: none;
  padding: 0;
  color: #0056b3;
  font: inherit;
  font-size: 0.9rem;
  text-decoration: underline;
  cursor: pointer;
`;

const PickerIntro = styled.p`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 1.5rem 0 0.75rem;

  @media (max-width: 1024px) {
    text-align: center;
  }
`;

const PickerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const PickerItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  width: 100%;
  text-align: left;
  background: rgba(255, 255, 255, 0.7);
  border: 2px solid transparent;
  padding: 0.85rem 1.1rem;
  font: inherit;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;

  &:hover {
    border-color: #47ABDB;
    background: #fff;
  }
`;

const PickerItemName = styled.span`
  font-weight: 700;
`;

const PickerItemMeta = styled.span`
  font-size: 0.85rem;
  color: #334155;
`;

const EmptyNote = styled.p`
  font-size: 1rem;
`;

const CallForAbstracts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/submissions/events')
      .then(({ data }) => {
        if (cancelled) return;
        const list = data || [];
        setEvents(list);
        const params = new URLSearchParams(location.search);
        const eventId = params.get('eventId');
        if (eventId && list.some((e) => String(e.id) === eventId)) {
          setSelectedEventId(eventId);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${t('callForAbstracts.title', 'Call for Abstracts')} - HEARTS Conference`;
    return () => { document.title = previousTitle; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intl.locale]);

  const selectedEvent = events.find((e) => String(e.id) === String(selectedEventId));

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleContinue = () => {
    if (!selectedEventId) return;
    navigate(`/submissions?eventId=${selectedEventId}`);
  };

  return (
    <PageWrapper>
      <Crumb aria-label="Breadcrumb">
        <Link to="/">{t('nav.home', 'Home')}</Link>
        <span className="sep">/</span>
        <span className="current">{t('callForAbstracts.breadcrumb', 'Call for Abstracts')}</span>
      </Crumb>

      <SectionWrapper>
        <SectionTitle>{t('callForAbstracts.title', 'Call for Abstracts')}</SectionTitle>
        <SectionSubTitle>
          {t('submissions.subtitle', 'Submit a scientific contribution for an upcoming HEARTS event.')}
        </SectionSubTitle>

        <EventWrapper>
          <ContentWrapper>
            <LeftColumn>
              <Headline>{t('callForAbstracts.headline', 'HEARTS MATCH — SUBMIT YOUR RESEARCH NOW')}</Headline>
              <Tagline>
                {t('callForAbstracts.tagline', 'Present your work. Find your match. Spark the next study.')}
              </Tagline>
              <IntroText>
                {t('callForAbstracts.intro', 'Previously presented research is welcome. Submit your abstract and add up to four ideas for future studies, clinical extensions, or multicenter collaborations.')}
              </IntroText>
              <IntroText>
                {t('callForAbstracts.introParagraph2', 'At HEARTS, your research does not end with the presentation — it can become the starting point for your next collaboration.')}
              </IntroText>
              <IntroText>
                {t('callForAbstracts.introParagraph3', 'Use your HEARTS Match to spark your next multicenter study.')}
              </IntroText>
              <IntroNote>
                {t('callForAbstracts.introNote', 'All career stages welcome.')}
              </IntroNote>

              {!selectedEvent && (
                <>
                  <PickerIntro>
                    {t('callForAbstracts.pickerIntro', 'Select an event:')}
                  </PickerIntro>
                  {loading ? (
                    <EmptyNote>{t('callForAbstracts.loadingEvents', 'Loading events…')}</EmptyNote>
                  ) : events.length === 0 ? (
                    <EmptyNote>
                      {t('callForAbstracts.pickerEmpty', 'There are no events currently accepting submissions. Please check back soon.')}
                    </EmptyNote>
                  ) : (
                    <PickerList>
                      {events.map((ev) => (
                        <PickerItem key={ev.id} type="button" onClick={() => setSelectedEventId(String(ev.id))}>
                          <PickerItemName>{ev.name}</PickerItemName>
                          {ev.dateTime && <PickerItemMeta>{formatDate(ev.dateTime)}</PickerItemMeta>}
                        </PickerItem>
                      ))}
                    </PickerList>
                  )}
                </>
              )}
            </LeftColumn>


            <MiddleColumn>
              <FlyerImage src={flyerPage1} alt={t('callForAbstracts.flyerAlt1', 'HEARTS MATCH information flyer, page 1 of 2')} />
              <FlyerImage src={flyerPage2} alt={t('callForAbstracts.flyerAlt2', 'HEARTS MATCH information flyer, page 2 of 2')} />
            </MiddleColumn>

            <RightColumn>
              <CtaButton onClick={handleContinue} disabled={!selectedEventId}>
                {t('event.submitAbstract', 'SUBMIT ABSTRACT')}
              </CtaButton>
              {selectedEvent ? (
                <>
                  <CtaCaption>
                    {t('callForAbstracts.selectedLabel', "You're submitting for")}: <strong>{selectedEvent.name}</strong>
                  </CtaCaption>
                  {selectedEvent.dateTime && <CtaCaptionMeta>{formatDate(selectedEvent.dateTime)}</CtaCaptionMeta>}
                  <ChangeLink type="button" onClick={() => setSelectedEventId(null)}>
                    {t('callForAbstracts.changeEvent', 'Not the right event? Choose another')}
                  </ChangeLink>
                </>
              ) : (
                <CtaHint>{t('callForAbstracts.continueHint', 'Select an event to continue')}</CtaHint>
              )}
            </RightColumn>
          </ContentWrapper>
        </EventWrapper>
      </SectionWrapper>
    </PageWrapper>
  );
};

export default CallForAbstracts;
